import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SocialLogin from "../SocialLogin/SocialLogin";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import {
  faEye,
  faEyeSlash,
  faCaretDown,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../../../components/Loader";
import i18n from "../../../i18n";
import { toast } from "react-toastify";
import { useTheme } from "../../../context/ThemeContext";
import secureStorage from "../../../utils/secureStorage";

function LoginIndex() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    // Only prefill if rememberMeChecked is true
    const savedRemember = secureStorage.getItem("rememberMeChecked") === "true";
    setRememberMe(savedRemember); // This will be false by default
    if (savedRemember) {
      const savedEmail = secureStorage.getItem("rememberMeEmail");
      // Never restore passwords - security best practice
      if (savedEmail) setEmail(savedEmail);
      // Password field should always start empty for security
      setPassword("");
    } else {
      setEmail("");
      setPassword("");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    // Save email to secureStorage if checked (NEVER store passwords)
    if (rememberMe) {
      secureStorage.setItem("rememberMeEmail", email);
      secureStorage.setItem("rememberMeChecked", "true");
    } else {
      secureStorage.removeItem("rememberMeEmail");
      secureStorage.setItem("rememberMeChecked", "false");
    }
    try {
      const success = await login(email, password);
      if (success) {
        window.location.href = "/home";
      } else {
        toast.error(t("invalid_email_or_password"));
        setError(t("invalid_email_or_password"));
      }
    } catch (err) {
      toast.error(t("an_error_occurred_during_login"));
      setError(t("an_error_occurred_during_login"));
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  return (
    <div className="flex h-screen px-4 sm:px-6 md:px-10 bg-white dark:bg-[#0A0C1C] overflow-hidden items-center justify-center">
      <div className="flex flex-col md:flex-row w-full ">
        {/* Login Section */}
        <div className="flex flex-col w-full items-center justify-center ">
          <div
            className="loginWrapper text-white  w-full sm:w-11/12 md:w-2/4 lg:w-2/5 xl:w-2/5 bg-fbfcfc rounded-xl px-5 sm:px-10 py-4 shadow-md"
            style={{ backgroundColor: isDarkMode ? "#0A0C1C" : "#6D29F6" }}
          >
            {/* Language Switcher */}

            {/* Logo */}
            <div className="text-center py-5 gap-2 flex flex-col items-center">
              <img src="/logo-bg.png" alt="VibeLinks Logo" className="h-16" />
              <h1 className="text-2xl font-bold">VibeLinks</h1>

            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="w-full">
              {/* Email */}
              <h1 className="text-lg font-bold mb-2">{t("welcome_back")}</h1>

              <div className="mb-2">
                <label className="text-xs mb-1">{t("email_address")}</label>
                <div className="relative">
                  <input
                    id="email"
                    type="text"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-2 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-700"
                    style={
                      isDarkMode
                        ? { backgroundColor: "#ffffff", color: "#000000" }
                        : { backgroundColor: "#ffffff", color: "#000000" }
                    }
                    placeholder={t("email_phone")}
                  />
                </div>
                {error?.email && (
                  <p className="text-purple-500 text-xs mt-1">
                    {error.email[0]}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className="mb-2">
                <label className="text-xs mb-1">{t("password")}</label>
                <div className="relative">
                  {/* Password Input */}
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-2 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-700"
                    style={
                      isDarkMode
                        ? { backgroundColor: "#ffffff", color: "#000000" }
                        : { backgroundColor: "#ffffff", color: "#000000" }
                    }
                    placeholder={t("enter_your_passwordd")}
                  />

                  {/* Eye Icon Toggle */}
                  <span
                    className="absolute text-purple-500 right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                  </span>
                </div>
                {error?.password && (
                  <p className="text-purple-500 text-xs mt-1">
                    {error.password[0]}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between mb-2">

                <label className="relative flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    name="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="peer sr-only"
                  />

                  <div
                    className={`h-5 w-5 border-2 rounded-md flex items-center justify-center 
                  transition-all duration-200 ${
                    rememberMe
                     ? isDarkMode 
                       ? "bg-white border-white" 
                       : "bg-[#6d29f6] border-white dark:border-[#6d29f6]"
                      : "bg-transparent border-white dark:border-[#6d29f6]"
                  }`}
                  >
                    {rememberMe && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill={isDarkMode ? "#000000" : "white"}
                        className="w-3 h-3"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-7.414 7.414a1 1 0 01-1.414 0L3.293 9.414a1 1 0 011.414-1.414L8.5 11.793l6.793-6.793a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <label
                    htmlFor="rememberMe"
                    className="text-xs text-white ml-2"
                  >
                    {t("remember_me")}
                  </label>
                </label>
                <div>
                  <Link
                    to="/forgot-password"
                    className="text-xs underline text-white"
                  >
                    {t("forgot_password")}
                  </Link>
                </div>
              </div>

              {/* Login button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 rounded-lg font-semibold hover:bg-opacity-90 transition duration-150 flex items-center justify-center"
                style={{
                  backgroundColor: isDarkMode ? "#6d29f6" : "white",
                  color: isDarkMode ? "white" : "#7c3aed",
                }}
              >
                {loading ? (
                  <>
                    <Loader loaderColor={isDarkMode ? "white" : "purple-700"} />
                    <span className="ml-2">{t("processing")}...</span>
                  </>
                ) : (
                  t("login")
                )}
              </button>
            </form>

            {/* Or Sign In With */}
            <div className="flex items-center justify-center">
              <div className="w-full border-t border-gray-300"></div>
              <p className="w-[500px] flex justify-center text-center px-1 py-4 text-sm">
                {t("or_sign_in_with")}
              </p>
              <div className="w-full border-t border-gray-300"></div>
            </div>
            {/* Social */}
            <div className="mt-1">
              <SocialLogin />
            </div>

            {/* Sign In Link */}
            <div className="text-center mt-8">
              <p className="text-sm">
                {t("signup_text")}{" "}
                <Link to="/register" className="underline font-medium">
                  {t("sign_up")}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginIndex;
